import React from 'react';
import PropTypes from 'prop-types';
import {
  FDAccordion,
  FDAccordionDetails,
  FDAccordionTitle,
  FDStack,
  FDTitleText,
} from '@facet-wealth/design';
import useMetaLayoutContext from 'Components/UI/Meta/MetaForm/Components/MetaLayoutContext';
import { MetaResourceRenderer } from 'Components/UI/Meta/MetaForm/Components/MetaResourceRenderer';
import LeverSetLayout from 'Views/Page/Content/RightSidebar/Content/Templates/RoadmapExplorerLevers/leverSetLayout';
import { buildUseStyles } from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/styles';
import LeverFieldLayout from './LeverLayouts/leverFieldLayout';
import { metaOverrideSet } from './metaOverrideSet';

const useStyles = buildUseStyles('LeversSidebarLayout', (theme) => ({
  topLeversContainer: {
    '&& .FD-MuiButtonBase-root.FD-MuiAccordionSummary-root': { // Main accordion summary
      padding: 0,
      paddingTop: theme.spacing(2),
      justifyContent: 'flex-start', // Align labels at start of accordion
    },
    '&&& .FD-MuiAccordionSummary-content': { // Accordion summary content
      margin: 0,
      // Make accordion titles left aligned instead of space-between'd
      flex: 'none',
      justifyContent: 'normal',
      alignItems: 'normal',
    },
    '&&& .FD-MuiAccordionSummary-expandIconWrapper': { // svg wrapper
      paddingLeft: 0,
      '& svg': { // chevron svg
        fontSize: theme.spacing(4),
      },
    },
  },
  subLeversContainer: { // this also gets styles from topLeversContainer
    '&& .FD-MuiButtonBase-root.FD-MuiAccordionSummary-root': { // Main accordion summary
      paddingTop: 0,
      minHeight: theme.spacing(4),
    },
    '&&& .FD-MuiAccordionSummary-content': { // Accordion summary content
      margin: 0,
      // Make accordion titles left aligned instead of space-between'd
      flex: 'none',
      justifyContent: 'normal',
      alignItems: 'normal',
    },
    '&&&&&&& .FD-MuiAccordionDetails-root': { // Accordion details
      paddingLeft: 0,
      paddingRight: 0,
      paddingBottom: 0,
    },
  },
}));

// Helper function to check if a field's value, label, or prompt matches the search term
export const matchesSearchTerm = (resource, searchTerm) => {
  if (!searchTerm) {
    return true;
  }
  const normalizedSearchTerm = searchTerm.toLowerCase();
  const label = resource.attr('label')?.toLowerCase();
  const value = resource.attr('value')?.toLowerCase();
  const prompt = resource.metaField('value')?.attr('prompt')?.toLowerCase();

  return (
    (label && label.includes(normalizedSearchTerm))
        || (value && value.includes(normalizedSearchTerm))
        || (prompt && prompt.includes(normalizedSearchTerm))
  );
};

// Recursive function to check for matching content in nested groups
export const hasMatchingContentInSubgroups = (group, searchTerm) => {
  const directLevers = group.attr('levers')?.resources()?.list() || [];
  if (directLevers.some(lever => matchesSearchTerm(lever, searchTerm))) {
    return true;
  }

  const leverSets = group.attr('leverSets')?.resources()?.list() || [];
  if (leverSets.some(set => (set.attr('levers')?.resources()?.list() || []).some(lever => matchesSearchTerm(lever, searchTerm)))) {
    return true;
  }

  const nestedGroups = group.attr('groups')?.resources()?.list() || [];
  return nestedGroups.some(nestedGroup => hasMatchingContentInSubgroups(nestedGroup, searchTerm));
};

const LeverSubAccordionLayout = ({
  expandedIDs, setExpandedIDs, loading, setLoading, setSaving, searchTerm, onTogglePinOptimistic, onLeverUpdate, isTopLevel = true,
}) => {
  const { resource } = useMetaLayoutContext();
  const classes = useStyles();

  const labelSize = isTopLevel ? 'lg' : 'md';

  // Determine if this component should be rendered
  const titleMatches = matchesSearchTerm(resource, searchTerm);
  const contentMatches = hasMatchingContentInSubgroups(resource, searchTerm);

  if (!titleMatches && !contentMatches) {
    return null;
  }

  // Filter child components for rendering
  const filteredGroups = resource.attr('groups')?.resources()?.list()?.map(groupRes => (
    <MetaResourceRenderer
      key={groupRes.id()}
      resource={groupRes}
      layout={LeverSubAccordionLayout}
      metaOverrideSet={metaOverrideSet}
      LayoutProps={{
        expandedIDs, setExpandedIDs, loading, setLoading, setSaving, searchTerm, onTogglePinOptimistic, onLeverUpdate, isTopLevel: false,
      }}
    />
  ))
    ?.filter(Boolean) || [];

  const filteredLeverSets = resource.attr('leverSets')?.resources()?.list()?.flatMap((leverSetRes) => {
    return (
      <FDStack direction={'column'} spacing={1}>
        <MetaResourceRenderer
          key={leverSetRes.id()}
          resource={leverSetRes}
          layout={LeverSetLayout}
          metaOverrideSet={metaOverrideSet}
          LayoutProps={{
            loading, setLoading, setSaving, matchesSearchTerm, searchTerm, onTogglePinOptimistic, onLeverUpdate,
          }}
        />
      </FDStack>
    );
  })
    ?.filter(Boolean)
    ?.flat() || [];

  const filteredLevers = resource.attr('levers')?.resources()?.list()?.map((leverRes) => {
    if (matchesSearchTerm(leverRes, searchTerm)) {
      return (
        <MetaResourceRenderer
          key={leverRes.id()}
          resource={leverRes}
          layout={LeverFieldLayout}
          metaOverrideSet={metaOverrideSet}
          LayoutProps={{
            loading, setLoading, setSaving, onTogglePinOptimistic, onLeverUpdate,
          }}
        />
      );
    }
    return null;
  })
    ?.filter(Boolean) || [];

  const hasVisibleChildren = filteredGroups.length > 0 || filteredLeverSets.length > 0 || filteredLevers.length > 0;
  const hasSearchTerm = !!searchTerm && searchTerm.trim() !== '';

  if (!titleMatches && !hasVisibleChildren) {
    return null;
  }

  return (
    <div id={`lever-group-${resource.id()}`} className={isTopLevel ? classes.topLeversContainer : classes.subLeversContainer}> {/* Wrapping in a box to avoid Accordion bar separator */ }
      <FDAccordion
        expansionID={resource.id()}
        expanded={expandedIDs.has(resource.id()) || hasSearchTerm}
        onChange={(expanded) => {
          const id = resource.id();
          setExpandedIDs?.((prev) => {
            const next = new Set(prev ?? []);
            if (expanded) {
              next.add(id);
            } else {
              next.delete(id);
            }
            return next;
          });
        }}
      >
        <FDAccordionTitle>
          <FDTitleText value={resource.attr('label')} color={'textSecondary'} size={labelSize} />
        </FDAccordionTitle>
        <FDAccordionDetails>
          <FDStack direction="column" spacing={1}>
            {filteredGroups}
            {filteredLeverSets}
            {filteredLevers}
          </FDStack>
        </FDAccordionDetails>
      </FDAccordion>
    </div>
  );
};

LeverSubAccordionLayout.propTypes = {
  expandedIDs: PropTypes.instanceOf(Set),
  setExpandedIDs: PropTypes.func,
  loading: PropTypes.bool,
  setLoading: PropTypes.func,
  setSaving: PropTypes.func,
  searchTerm: PropTypes.string,
  onTogglePinOptimistic: PropTypes.func,
  onLeverUpdate: PropTypes.func,
  isTopLevel: PropTypes.bool,
};

export default LeverSubAccordionLayout;
